import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api } from '../../api/client.js';
import { DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { StatTile } from '../../components/charts.jsx';
import { MoneySummary } from '../../components/refunds.jsx';
import { MainMeterSummary } from '../../components/mainMeter.jsx';
import { WelcomeBanner } from '../../components/WelcomeBanner.jsx';
import { useMeta } from '../../state/MetaContext.jsx';
import { useAuth } from '../../state/AuthContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatXaf } from '../../lib/format.js';

const blankService = {
  kind: 'electricity',
  name: '',
  meterNumber: '',
  openingReading: '',
  readingDay: '1',
  billingMode: 'metered',
  unitCostXaf: '',
  standingChargeXaf: '0',
  flatAmountXaf: ''
};

/**
 * Setting a service up, and changing what it charges afterwards.
 *
 * The same form does both because they are the same set of figures. Editing is
 * the landlord's alone - a manager reads the meter, they do not decide the
 * price - and the API refuses them too.
 */
export const ServiceForm = ({ service = null, onSubmit, onClose }) => {
  const { utilityKinds, utilityBillingModes } = useMeta();
  const editing = Boolean(service);
  const [form, setForm] = useState(
    editing
      ? {
          kind: service.kind,
          name: service.name,
          meterNumber: service.meterNumber || '',
          openingReading: String(service.openingReading ?? ''),
          readingDay: String(service.readingDay ?? 1),
          billingMode: service.billingMode,
          unitLabel: service.unitLabel,
          unitCostXaf: String(service.unitCost ?? ''),
          standingChargeXaf: String(service.standingCharge ?? '0'),
          flatAmountXaf: String(service.flatAmount ?? '')
        }
      : blankService
  );
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const metered = form.billingMode === 'metered';
  // A compound's light is sold on from the landlord's own ENEO meter, so that
  // meter is where setting one up begins: its number and where the dial stands
  // today. The submeters are put on it afterwards.
  const needsMainMeter = !editing && metered && form.kind === 'electricity';
  const rateChanged =
    editing &&
    (Number(form.unitCostXaf || 0) !== service.unitCost ||
      Number(form.standingChargeXaf || 0) !== service.standingCharge ||
      Number(form.flatAmountXaf || 0) !== service.flatAmount);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        // The kind is what the service is, not what it costs, and changing it
        // under bills already raised would make their labels lie.
        ...(editing ? {} : { kind: form.kind }),
        name: form.name.trim(),
        meterNumber: form.meterNumber.trim() || null,
        ...(needsMainMeter ? { openingReading: Number(form.openingReading || 0) } : {}),
        readingDay: Number(form.readingDay || 1),
        billingMode: form.billingMode,
        unitCostXaf: metered ? Number(form.unitCostXaf || 0) : 0,
        standingChargeXaf: Number(form.standingChargeXaf || 0),
        flatAmountXaf: metered ? 0 : Number(form.flatAmountXaf || 0)
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `What ${service.name} charges, and the meter it charges from` : 'Add a utility service'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="utility-service-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : editing ? 'Save' : 'Add the service'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      {rateChanged ? (
        <div className="notice notice-info">
          <strong>Bills already raised keep the old rate.</strong> Every bill carries the figures it was worked out
          from, so changing the price here only affects readings taken from now on.
        </div>
      ) : null}

      <form id="utility-service-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="What is it" error={errors.kind}>
            <select
              value={form.kind}
              onChange={(event) => set({ kind: event.target.value })}
              disabled={editing}
            >
              {utilityKinds.map((kind) => (
                <option key={kind.slug} value={kind.slug}>
                  {kind.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Name it" hint="Something you will recognise: ENEO meter - Mile 17." error={errors.name} required>
            <input type="text" value={form.name} onChange={(event) => set({ name: event.target.value })} required />
          </Field>
        </div>

        {needsMainMeter ? (
          <div className="notice notice-info">
            <strong>Start with the main meter.</strong> This is the dial the utility bills you on, and every submeter
            under it is only meaningful against it. Record it here, then put the people who use it on it.
          </div>
        ) : null}

        <div className={needsMainMeter ? 'grid-2' : ''}>
          <Field
            label={needsMainMeter ? 'Main meter number' : 'Meter number'}
            hint="The number on the meter itself, as it appears on the utility's bill. It is what a tenant can check your figures against."
            error={errors.meterNumber}
            required={needsMainMeter}
          >
            <input
              type="text"
              value={form.meterNumber}
              onChange={(event) => set({ meterNumber: event.target.value })}
              placeholder="20752599"
              required={needsMainMeter}
            />
          </Field>

          {needsMainMeter ? (
            <Field
              label="What it reads today"
              hint="Where the dial stands now. Everything billed off this meter counts up from here, and it cannot be changed afterwards."
              error={errors.openingReading}
              required
            >
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.openingReading}
                onChange={(event) => set({ openingReading: event.target.value })}
                required
              />
            </Field>
          ) : null}
        </div>

        {metered ? (
          <Field
            label="Day of the month the meters are read"
            hint="On this day the month's round opens by itself and lands on your worklist, with every meter still to read counted. 28 at the most, so it works in February."
            error={errors.readingDay}
          >
            <input
              type="number"
              min="1"
              max="28"
              step="1"
              value={form.readingDay}
              onChange={(event) => set({ readingDay: event.target.value })}
            />
          </Field>
        ) : null}

        <Field label="How is it charged" error={errors.billingMode}>
          <select value={form.billingMode} onChange={(event) => set({ billingMode: event.target.value })}>
            {utilityBillingModes.map((mode) => (
              <option key={mode.slug} value={mode.slug}>
                {mode.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          {metered ? (
            <Field label="Cost per unit in FCFA" error={errors.unitCostXaf} required>
              <input
                type="number"
                min="1"
                step="1"
                value={form.unitCostXaf}
                onChange={(event) => set({ unitCostXaf: event.target.value })}
                required
              />
            </Field>
          ) : (
            <Field label="Amount each period in FCFA" error={errors.flatAmountXaf} required>
              <input
                type="number"
                min="1"
                step="1"
                value={form.flatAmountXaf}
                onChange={(event) => set({ flatAmountXaf: event.target.value })}
                required
              />
            </Field>
          )}

          <Field
            label="Standing charge in FCFA"
            hint="Meter rental or your share of the fixed charge. Leave at zero if there is none."
            error={errors.standingChargeXaf}
          >
            <input
              type="number"
              min="0"
              step="1"
              value={form.standingChargeXaf}
              onChange={(event) => set({ standingChargeXaf: event.target.value })}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
};

const Utilities = () => {
  useTitle('Utilities');
  const { isLandlord } = useAuth();
  const { utilityKindLabel } = useMeta();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const services = useFetch('/landlord/utility-services');
  const tasks = useFetch('/landlord/utility-tasks');

  if (services.loading && !services.data) return <Spinner />;
  if (services.error) {
    return (
      <div className="container section">
        <ErrorState message={services.error} onRetry={services.reload} />
      </div>
    );
  }

  const items = services.data.items;
  const claims = tasks.data?.claims?.length || 0;
  const pending =
    claims +
    (tasks.data?.disconnects.length || 0) +
    (tasks.data?.reconnects.length || 0) +
    (tasks.data?.readingRounds?.length || 0);
  const outstanding = items.reduce((total, service) => total + (service.outstanding || 0), 0);
  // Money taken against bills that were later cancelled. It belongs to the
  // customer, so it is counted on its own rather than netted off what is owed -
  // a credit printed under "Outstanding" reads as a negative debt and is simply
  // wrong.
  const credit = items.reduce((total, service) => total + (service.credit || 0), 0);
  const customers = items.reduce((total, service) => total + (service.accountCount || 0), 0);
  const billed = items.reduce((total, service) => total + (service.billed || 0), 0);
  const paid = items.reduce((total, service) => total + (service.paid || 0), 0);
  const refunded = items.reduce((total, service) => total + (service.refunded || 0), 0);
  // The main meters against the utilities themselves: charged, paid, still owed.
  const metered = items.filter((service) => service.main);
  const main = metered.reduce(
    (total, service) => ({
      charged: total.charged + service.main.charged,
      paid: total.paid + service.main.paid,
      owed: total.owed + service.main.owed,
      ahead: total.ahead + service.main.ahead
    }),
    { charged: 0, paid: 0, owed: 0, ahead: 0 }
  );
  const utilityName = [...new Set(metered.map((service) => service.name))].join(' / ') || 'the utility';
  const cutOff = items.reduce((total, service) => total + (service.cutOffCount || 0), 0);

  const create = async (payload) => {
    await api.post('/landlord/utility-services', payload);
    toast.success('Service added. Now put the people who use it on it.');
    setAdding(false);
    services.reload();
  };

  const columns = [
    {
      key: 'name',
      header: 'Service',
      render: (service) => (
        <div className="cell-stack">
          <Link to={`/landlord/utilities/${service.id}`} className="cell-title">
            {service.name}
          </Link>
          <span className="muted">{utilityKindLabel(service.kind)}</span>
        </div>
      )
    },
    {
      key: 'rate',
      header: 'Charged at',
      render: (service) => (
        <div className="cell-stack">
          {service.billingMode === 'metered' ? (
            <strong>
              {formatXaf(service.unitCost)} per {service.unitLabel}
            </strong>
          ) : (
            <strong>{formatXaf(service.flatAmount)} each period</strong>
          )}
          {service.standingCharge > 0 ? (
            <span className="muted">plus {formatXaf(service.standingCharge)} standing charge</span>
          ) : null}
        </div>
      )
    },
    {
      key: 'customers',
      header: 'On the meter',
      render: (service) => (
        <div className="cell-stack">
          <span>
            {service.accountCount} {service.accountCount === 1 ? 'customer' : 'customers'}
          </span>
          {service.cutOffCount > 0 ? <span className="overdue-flag">{service.cutOffCount} cut off</span> : null}
        </div>
      )
    },
    {
      key: 'owed',
      header: 'Outstanding',
      render: (service) => (
        <div className="cell-stack">
          {service.outstanding > 0 ? (
            <strong className="owing">{formatXaf(service.outstanding)}</strong>
          ) : (
            <span className="muted">Nothing owing</span>
          )}
          {service.credit > 0 ? (
            <span className="money-amount is-out">{formatXaf(service.credit)} overpaid</span>
          ) : null}
          <span className="muted">
            {formatXaf(service.billed || 0)} billed, {formatXaf(service.paid || 0)} paid
          </span>
          {service.main ? (
            <span className={service.main.owed > 0 ? 'owing' : 'muted'}>
              {service.main.owed > 0 ? `${formatXaf(service.main.owed)} still owed to ${service.name}` : `Main meter paid up with ${service.name}`}
            </span>
          ) : null}
        </div>
      )
    },
    {
      key: 'status',
      header: '',
      render: (service) =>
        service.isActive ? null : <StatusBadge status="withdrawn" label="Archived" />
    }
  ];

  return (
    <div className="container section">
      <WelcomeBanner
        eyebrow="Your meters and bills"
        title="Utilities"
        text="Light, water and internet you sell on - to your tenants and to anybody else on your meter."
      >
        {isLandlord ? (
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add a service
          </button>
        ) : null}
        <Link to="/landlord/utilities/tasks" className="btn btn-outline">
          Worklist{pending > 0 ? ` (${pending})` : ''}
        </Link>
      </WelcomeBanner>

      {pending > 0 ? (
        <div className="notice notice-warn">
          <strong>
            {pending} {pending === 1 ? 'job is' : 'jobs are'} waiting on somebody.
          </strong>{' '}
          {claims > 0 ? (
            <>
              {claims} {claims === 1 ? 'payment needs' : 'payments need'} confirming.{' '}
            </>
          ) : null}
          <Link to="/landlord/utilities/tasks">Open the worklist</Link>.
        </div>
      ) : null}

      {metered.length ? (
        <>
          <h2 className="money-heading">Main meter - what you owe {utilityName}</h2>
          <MainMeterSummary utility={utilityName} {...main} />
        </>
      ) : null}

      <h2 className="money-heading">Your customers - what they owe you</h2>
      <MoneySummary billed={billed} paid={paid} owed={outstanding} credit={credit} refunded={refunded} />
      {credit > 0 ? (
        <p className="muted money-hint">
          Somebody has paid more than they were billed. Open the service to see who - each overpaid customer has a Refund
          button.
        </p>
      ) : null}

      <div className="stat-row">
        <StatTile value={items.length} label="Services" />
        <StatTile value={customers} label="Customers on them" />
        <StatTile value={cutOff} label="Cut off" tone={cutOff > 0 ? 'warn' : undefined} />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No utility services yet',
          message:
            'Add the meter or subscription you sell on, then put your tenants and neighbours on it. ' +
            'Readings, bills, receipts and cut-offs all follow from there.'
        }}
      />

      {adding ? <ServiceForm onSubmit={create} onClose={() => setAdding(false)} /> : null}
    </div>
  );
};

export default Utilities;
